import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { ImpersonationBannerComponent } from '../impersonation-banner/impersonation-banner.component';

/**
 * The default layout, with the Freely chrome made optional by query string.
 *
 * `?displayHeader=false` / `?displayFooter=false` strip the header and footer so a page can be
 * hosted inside another application — the Freely Windows agent embeds the customer detail view
 * this way, where a second navigation bar and a copyright footer are noise around someone else's
 * window.
 *
 * A separate layout rather than a flag on `LayoutDefaultComponent`, so a query parameter can never
 * accidentally strip the chrome from a screen that was not built to be embedded.
 *
 * **Chrome is the only thing this controls.** The route still carries `UserAuthGuard` and
 * `PermissionGuard`: hiding the header does not make a page public, and an embedding application
 * still needs a signed-in Freely session in that browser context.
 */
@Component({
  selector: 'app-layout-embeddable',
  standalone: true,
  templateUrl: './layout-embeddable.component.html',
  host: {
    class: 'layout layout-default',
  },
  imports: [RouterOutlet, HeaderComponent, FooterComponent, NgClass, ImpersonationBannerComponent],
})
export class LayoutEmbeddableComponent {
  private readonly route = inject(ActivatedRoute);

  /**
   * Query parameters, watched rather than read once: an embedding host may navigate between
   * customers without reloading, and the chrome must follow.
   */
  private readonly params = toSignal(this.route.queryParams, { initialValue: {} as Record<string, string> });

  /**
   * Both default to SHOWN. Only an explicit `false` hides the chrome, so a mistyped or absent
   * parameter leaves a normal Freely page rather than a headless one nobody can navigate out of.
   */
  readonly showHeader = computed(() => this.params()['displayHeader'] !== 'false');
  readonly showFooter = computed(() => this.params()['displayFooter'] !== 'false');
}
